import { useState } from 'react';
import type { AccountSecret } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';

export interface SecretRowProps {
  secret: AccountSecret;
  onEdit: (secret: AccountSecret) => void;
  onDelete: (secret: AccountSecret) => void;
  /** Injected so this component stays presentational and story-testable. */
  onReveal: (secret: AccountSecret) => Promise<string>;
  disabled?: boolean;
}

const MASK = '••••••••';

export function SecretRow({ secret, onEdit, onDelete, onReveal, disabled }: SecretRowProps) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reveal = async () => {
    setRevealing(true);
    setError(null);
    try {
      setRevealed(await onReveal(secret));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reveal this secret');
    } finally {
      setRevealing(false);
    }
  };

  const hide = () => {
    setRevealed(null);
    setError(null);
  };

  return (
    <li className="flex items-start justify-between gap-4 p-3" data-testid={`secret-row-${secret.name}`}>
      <button
        type="button"
        className="min-w-0 flex-1 text-left"
        onClick={() => onEdit(secret)}
        disabled={disabled}
        data-testid={`secret-edit-${secret.name}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-label font-medium">{secret.name}</span>
          {secret.workspace_id && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-caption text-muted-foreground">
              workspace-tagged
            </span>
          )}
        </div>
        {secret.description && (
          <p className="mt-0.5 truncate text-caption text-muted-foreground">{secret.description}</p>
        )}
      </button>

      <div className="flex shrink-0 items-center gap-2">
        <code
          className="max-w-[18rem] truncate rounded bg-muted px-2 py-1 font-mono text-caption"
          data-testid={`secret-value-${secret.name}`}
        >
          {revealed ?? (secret.value_hint ? `${MASK}${secret.value_hint}` : MASK)}
        </code>
        {revealed === null ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={reveal}
            disabled={disabled || revealing}
            data-testid={`secret-reveal-${secret.name}`}
          >
            {revealing ? 'Revealing…' : 'Reveal'}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={hide}
            data-testid={`secret-hide-${secret.name}`}
          >
            Hide
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(secret)}
          disabled={disabled}
          data-testid={`secret-delete-${secret.name}`}
        >
          Delete
        </Button>
      </div>

      {error && (
        <p className="text-caption text-destructive" data-testid={`secret-reveal-error-${secret.name}`}>
          {error}
        </p>
      )}
    </li>
  );
}
